import { SupportedCountry } from "@brexhq/component-library";
import Cookies from "js-cookie";
import * as React from "react";
import {
  FormState,
  UseFormGetValues,
  UseFormTrigger,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
} from "react-hook-form";

import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import useLocation from "@brexhq/next/src/hooks/useLocation";

import {
  PartnerRefValues,
  shouldPrefillCustomerReferral,
  UserPrefillData,
} from "../../../routes/signup/Form";

import { useLeadFormFields } from "./hooks/useLeadFormFields";
import { employeeGreaterThan50 } from "./machines/helpers";
import {
  leadFormEvents,
  leadFormMachineContext,
  LeadFormStateMachineSend,
  LeadFormStateMachineState,
  LeadFormSteps,
} from "./machines/types";
import { Fields, LeadFormValues, FormFields, FormType } from "./sharedTypes";
import { useGetLead } from "./utils";

type SubmitArgs = {
  event: React.FormEvent;
  fields: Extract<keyof LeadFormValues, string>[];
  prevStep?: LeadFormSteps;
};

export enum StepTransitionDirection {
  NEXT = 1,
  PREV = -1,
}

export type ErrorMessage = {
  title: string;
  message: React.ReactNode;
};

type FormContext = {
  getValues: UseFormGetValues<LeadFormValues>;
  trigger: UseFormTrigger<LeadFormValues>;
  setValue: UseFormSetValue<LeadFormValues>;
  resetField: (field: keyof LeadFormValues | (keyof LeadFormValues)[]) => void;
  fields: Fields;
  formState: FormState<LeadFormValues>;
  handleSubmit: UseFormHandleSubmit<LeadFormValues>;
  programName: string;
  setProgress: React.Dispatch<React.SetStateAction<number>>;
  setShowFinalStateView: React.Dispatch<React.SetStateAction<boolean>>;
  country: SupportedCountry;
  clearErrors: UseFormClearErrors<LeadFormValues>;
  control: Control<LeadFormValues, any>;
  currentState: string;
};

type SubmissionContext = {
  submit: (args: SubmitArgs) => void;
  prev: (field: keyof LeadFormValues | (keyof LeadFormValues)[]) => void;
  submitAttempted: boolean;
  submitError: null | ErrorMessage;
  isSubmitting: boolean;
  stepTransitionDirection: StepTransitionDirection;
  setSubmitError: React.Dispatch<React.SetStateAction<null | ErrorMessage>>;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  stateMachineCtx: leadFormMachineContext;
  form: FormContext;
  state: LeadFormStateMachineState;
};

export const FormSubmissionContext = React.createContext<SubmissionContext>(
  {} as SubmissionContext,
);

export const FormSubmissionProvider = ({
  children,
  send,
  state,
  programName,
  setProgress,
  setShowFinalStateView,
  stateMachineCtx,
  userPrefillData,
  formType,
  mode = "onChange",
}: {
  children: React.ReactNode;
  send: LeadFormStateMachineSend;
  state: LeadFormStateMachineState;
  programName: string;
  setProgress: React.Dispatch<React.SetStateAction<number>>;
  stateMachineCtx: leadFormMachineContext;
  setShowFinalStateView: React.Dispatch<React.SetStateAction<boolean>>;
  partnerRef?: PartnerRefValues | null;
  userPrefillData?: UserPrefillData;
  formType: FormType;
  mode?: "onChange" | "onSubmit" | "onBlur";
}) => {
  const [isValidCustomerReferral, setIsValidCustomerReferral] =
    React.useState<boolean>(false);
  const location = useLocation();
  const [submitAttempted, setSubmitAttempted] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<null | ErrorMessage>(
    null,
  );
  const [stepTransitionDirection, setStepTransitionDirection] = React.useState(
    StepTransitionDirection.NEXT,
  );
  const leadToken = Cookies.get("leadToken");
  const hasFetchedBefore = React.useRef(false);
  const isInitialFetch = React.useRef(false);
  const getLead = useGetLead();

  const { trackEvent } = useAnalytics();
  /**
   * TODO: [M7] deprecate once experiment is over
   */
  const leadForm = useLeadFormFields(trackEvent, {
    requiredFields: true,
    formType,
    mode,
  });
  /**
   * NOTE: We're retrieving only the functions we're using but we can retrieve
   * more if needed. Might be better to destructure everything available for simplicity
   */
  const {
    getValues,
    trigger,
    setValue,
    fields,
    formState,
    handleSubmit,
    country,
    clearErrors,
    resetField,
    control,
  } = leadForm;

  React.useEffect(() => {
    if (submitError) {
      console.error(submitError);
    }
  }, [submitError]);

  /**
   * Streamline acct creation, we store info on lead if it already exists
   * through the leadToken value stored in cookies
   */
  const handleGetLead = React.useCallback(async () => {
    /**
     * we want to handle fetching plus prefilling the forms.
     */
    if (leadToken && !userPrefillData?.attributionToken) {
      const { leadDetails } = await getLead(leadToken);
      if (!hasFetchedBefore.current) {
        isInitialFetch.current = true;
        hasFetchedBefore.current = true;
      }

      if (leadDetails) {
        Object.keys(fields).forEach((key) => {
          if (key === "workEmail") {
            setValue("workEmail", leadDetails?.email);
          } else if (key === "mobileNumber" && leadDetails?.phoneNumber) {
            setValue("mobileNumber", leadDetails?.phoneNumber);
          } else if (key === "isEcomm") {
            setValue(
              "isEcomm",
              leadDetails?.segmentationDataV3?.companyType
                ? leadDetails?.segmentationDataV3?.companyType
                : leadDetails?.leadSelfAttestedType,
            );
          } else if (key === "monthlySales") {
            setValue(
              "monthlySales",
              leadDetails?.segmentationDataV3?.salesVolume
                ? leadDetails?.segmentationDataV3?.salesVolume
                : leadDetails?.salesVolumeV2,
            );
          } else if (key === "employeeCount") {
            setValue(
              "employeeCount",
              leadDetails?.segmentationDataV3?.employeeCount,
            );
          } else if (key === "fundingSources") {
            setValue(
              "fundingSources",
              leadDetails?.segmentationDataV3?.fundingSources
                ? leadDetails?.segmentationDataV3?.fundingSources
                : leadDetails?.fundingSourcesV2,
            );
          } else {
            if (leadDetails?.[key]) {
              setValue(key as any, leadDetails?.[key]);
            }
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadToken, userPrefillData?.attributionToken]);

  /**
   * Existing lead
   */
  React.useEffect(() => {
    if (isInitialFetch.current) {
      const vals = getValues();
      isInitialFetch.current = false;

      let isValid =
        !!vals.firstName &&
        !!vals.lastName &&
        !!vals.companyName &&
        !!vals.mobileNumber &&
        !!vals.employeeCount;
      if (!!vals.employeeCount && !employeeGreaterThan50(vals.employeeCount)) {
        isValid =
          vals.isEcomm !== undefined &&
          !!vals.monthlySales &&
          !!vals.fundingSources &&
          !!vals.fundingSources.length;
      }
      // If the lead already exists and they've filled out all the fields, just ask for confirmation
      if (isValid) {
        send({ type: leadFormEvents.existingLead });
      }
    }
  }, [send, getValues, fields]);

  /**
   * partner prefill
   */
  React.useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const cookiePrefToken = Cookies.get("pref_token");
    const prefToken = (params?.get("pref_token") as string) || cookiePrefToken;

    // We only want to prefill the values if we're on User info.
    // If we force values on other steps, any manual changes to
    // user inputted values will be reset
    const isFirstStep = state.matches(LeadFormSteps.userInfo);
    if (prefToken && isFirstStep) {
      Object.keys(fields).forEach((key) => {
        if (userPrefillData?.[key]) {
          setValue(key as any, userPrefillData?.[key]);
        }
      });
    } else {
      handleGetLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGetLead, location?.search, userPrefillData]);

  /**
   * Customer Referral codes
   */
  React.useEffect(() => {
    if (isValidCustomerReferral) {
      setValue("refCode", Cookies.get("rc"));
    }
  }, [isValidCustomerReferral, setValue]);

  const prev = React.useCallback(
    (field: keyof LeadFormValues | (keyof LeadFormValues)[]) => {
      resetField(field);
      setStepTransitionDirection(StepTransitionDirection.PREV);
      setSubmitError(null);
      send({ type: leadFormEvents.back });
    },
    [resetField, send],
  );

  // We want to prefill the self attested code only if its a valid customer referral
  // The shouldPrefillCustomerReferral checks againts the cookie "rc"
  React.useEffect(() => {
    async function checkValidCustomerReferral() {
      const isValidCustomerReferral = await shouldPrefillCustomerReferral(
        Cookies.get("rc") || "",
      );
      setIsValidCustomerReferral(isValidCustomerReferral);
    }
    checkValidCustomerReferral();
  }, []);

  /**
   * TODO: Comment more on the onSubmit / onError behaviours
   */
  const submit = React.useCallback(
    ({ event, fields, prevStep }: SubmitArgs) => {
      setStepTransitionDirection(StepTransitionDirection.NEXT);
      const onSubmit = () => {
        clearErrors();
        send({
          type: leadFormEvents.submit,
          value: { ...getValues(), country, prevStep },
        });
      };
      const onError = (errors, _, fields) => {
        /**
         * Fields not created on the form are validated as well,
         * so we have to manually pick out the errors we want
         * to care about
         * (Line by line)
         * 1. get the error list
         * 2. get the full fields (all the fields in the lead form)
         * 3. retrieve the field list for this form
         * 4. see if the errors are related to only the field list in question, and remove
         * the field errors that don't apply.
         */
        const errorList = { ...errors };
        const fullFieldList: FormFields[] = Object.keys(
          FormFields,
        ) as FormFields[];

        const filteredList = fullFieldList.filter(
          (field) => !fields.includes(field),
        );
        filteredList.forEach((field) => {
          delete errorList[field];
        });

        // Want to make sure that we clear validation on the webiste if user has no website
        if (errorList.website && getValues("hasNoWebsite")) {
          delete errorList.website;
        }
        /**
         * Set error if the error is applicable, else send it off
         */
        if (Object.keys(errorList).length) {
          setSubmitError({
            title: "Missing information",
            message:
              "Complete the required fields and correct any errors to continue.",
          });
        } else {
          clearErrors();
          setSubmitError(null);
          send({
            type: leadFormEvents.submit,
            value: { ...getValues(), country, prevStep },
          });
        }
      };

      event.preventDefault();
      setSubmitAttempted(true);
      /**
       * https://react-hook-form.com/api/useform/handlesubmit
       */
      handleSubmit(onSubmit, (errors, e) => onError(errors, e, fields))(event);
    },
    [handleSubmit, send, getValues, country, clearErrors],
  );

  const currentStateList = state.toStrings();
  const currentState = currentStateList[currentStateList.length - 1];

  return (
    <FormSubmissionContext.Provider
      value={{
        submit,
        prev,
        submitAttempted,
        submitError,
        isSubmitting,
        setSubmitError,
        stateMachineCtx,
        setIsSubmitting,
        stepTransitionDirection,
        state,
        form: {
          getValues,
          trigger,
          setValue,
          fields,
          formState,
          handleSubmit,
          programName,
          setProgress,
          setShowFinalStateView,
          country,
          clearErrors,
          resetField,
          control,
          currentState,
        },
      }}
    >
      {children}
    </FormSubmissionContext.Provider>
  );
};

/**
 *
 * @returns
 * submit,
 * submitAttempted,
 * submitError,
 * isSubmitting,
 * setSubmitError,
 * setIsSubmitting,
 * form: {
 *  getValues,
 *  trigger,
 *  setValue,
 *  fields,
 *  formState,
 *  handleSubmit,
 *  programName,
 *  setProgress,
 *  country,
 *  clearErrors
 * }
 */
export const useFormSubmissionContext = () => {
  const formSubmissionContext = React.useContext(FormSubmissionContext);
  return {
    ...formSubmissionContext,
  };
};
